import React, { useEffect, useRef, useState } from "react";
import logo from "../assets/logos/hobo-logo.png";
import influencerAvatar from "../assets/influencer-avatar.svg";
import influencerLogout from "../assets/icons/influencer-logout.svg";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Config } from "../.env.js";
import { clearInfluencerDashboardState } from "../utils/influencerDashboardState";

export const InfluencerHeader = ({
  influencer: propInfluencer,
  activeTab,
  setActiveTab,
}) => {
  const [underlineStyle, setUnderlineStyle] = useState({ left: 0, width: 0 });
  const itemRefs = useRef([]);
  const navContainerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [openMobileMenu, setOpenMobileMenu] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // const [navItems, setNavItems] = useState(navigationItems);
  const [influencer, setInfluencer] = useState(() => {
    // initialize from prop if exists, else from localStorage
    if (propInfluencer) return propInfluencer;
    try {
      return JSON.parse(localStorage.getItem("influencer")) || null;
    } catch {
      return null;
    }
  });

  // console.log("Influencer:", influencer);
  // console.log("Active Tab:", activeTab);

  // Close profile dropdown on outside clicks
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenProfile(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const updateUnderline = () => {
      let activeIndex;

      if (activeTab) {
        // ✅ Use activeTab when passed
        activeIndex = [
          "All Campaigns",
          "My Applied Campaigns",
          "My Dashboard",
        ].indexOf(activeTab);
      } else {
        // ✅ Fallback to route-based detection
        if (location.pathname.startsWith("/brand-collaboration-live-campaigns"))
          activeIndex = 0;
        else if (location.pathname.includes("dashboard")) activeIndex = 2;
      }

      const activeEl = itemRefs.current[activeIndex];
      if (activeEl) {
        const rect = activeEl.getBoundingClientRect();
        const containerRect = activeEl.parentElement.getBoundingClientRect();
        setUnderlineStyle({
          left: rect.left - containerRect.left,
          width: activeEl.offsetWidth,
        });
      }
    };

    updateUnderline();
    window.addEventListener("resize", updateUnderline);
    return () => window.removeEventListener("resize", updateUnderline);
  }, [activeTab, location.pathname]);

  const goToView = (viewParam) => {
    const isLiveRoute = location.pathname.startsWith(
      "/brand-collaboration-live-campaigns"
    );
    const params = isLiveRoute
      ? new URLSearchParams(location.search)
      : new URLSearchParams();
    params.set("view", viewParam);
    navigate(`/brand-collaboration-live-campaigns?${params.toString()}`);
  };

  const handleLogoutInfluencer = async () => {
    try {
      // Call the logout API
      const response = await axios.post(
        `${Config.API_URL}/logout`,
        {},
        { withCredentials: true }
      );

      if (response.status === 200) {
        // console.log(response.data.message); // "Logout successful"
        // localStorage.removeItem("isAuthenticated");
        clearInfluencerDashboardState();
        localStorage.removeItem("token");
        localStorage.clear();
        // Redirect to login page
        // navigate("/");
        window.location.href = Config.SITE_URL;
      }
    } catch (error) {
      console.error("Error during logout:", error);
      alert("Logout failed. Please try again.");
    }
  };

  return (
    <header
      className="bg-white w-full flex justify-center shadow-sm"
      role="banner"
    >
      <div className="w-full h-16 flex items-center justify-between px-[10px] lg:px-[90px] relative">
        {/* Logo */}
        <Link to="/">
          <img
            src={logo}
            alt="Company Logo"
            width={2040}
            height={668}
            className="h-10 sm:h-14 lg:h-[55px] w-auto"
          />
        </Link>
        {/* ✅ Mobile menu button */}
        <button
          type="button"
          className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
          onClick={() => setOpenMobileMenu(!openMobileMenu)}
          aria-label={openMobileMenu ? "Close mobile menu" : "Open mobile menu"}
          aria-expanded={openMobileMenu}
          aria-controls="influencer-mobile-menu"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={
                openMobileMenu
                  ? "M6 18L18 6M6 6l12 12"
                  : "M4 6h16M4 12h16M4 18h16"
              }
            />
          </svg>
        </button>

        {/* ✅ Mobile dropdown with activeTab */}
        {openMobileMenu && (
          <div
            id="influencer-mobile-menu"
            className="absolute top-16 left-0 w-full bg-white shadow-md flex flex-col gap-4 px-6 py-4 lg:hidden z-50"
          >
            {["All Campaigns", "My Applied Campaigns", "My Dashboard"].map(
              (item) => (
                <button
                  key={item}
                  onClick={() => {
                    const viewParam =
                      item === "My Dashboard"
                        ? "my-dashboard"
                        : item === "My Applied Campaigns"
                        ? "my-applied-campaigns"
                        : "all-campaigns";
                    // navigate(`/brand-collaboration-live-campaigns?view=${viewParam}`);
                    goToView(viewParam);
                    setOpenMobileMenu(false);
                  }}
                  className={`${
                    activeTab === item
                      ? "font-medium text-[#2b2f34]"
                      : "font-normal text-gray-600"
                  } text-base text-left hover:text-blue-600 transition-colors`}
                >
                  {item}
                </button>
              )
            )}

            {/* ✅ Auth/Profile inside mobile dropdown */}
            {localStorage.getItem("influencer") ? (
              <>
                <div className="flex items-center gap-3">
                  <img
                    src={influencerAvatar}
                    alt="Profile"
                    width={40}
                    height={40}
                    className="w-10 h-10 rounded-full object-cover border border-black"
                  />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900">
                      {JSON.parse(localStorage.getItem("influencer"))
                        ?.FullName || "User"}
                    </span>
                    <span className="text-xs text-gray-500">Influencer</span>
                  </div>
                </div>
                <a
                  href={`${Config.SITE_URL}/creators/profile`}
                  className="flex items-center gap-2 w-full py-3 text-sm text-gray-700 hover:bg-blue-50"
                >
                  <i className="fa-regular fa-id-card w-4 text-center" />
                  My Profile
                </a>
                <button
                  className="flex items-center gap-2 w-full py-3 text-sm text-gray-700 hover:bg-blue-50"
                  onClick={handleLogoutInfluencer}
                >
                  <img src={influencerLogout} alt="" width={16} height={16} />
                  Logout
                </button>
              </>
            ) : (
              <div className="flex flex-col gap-2">
                <a
                  href={`${Config.FRONTEND_URL}/influencer-login`}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700"
                >
                  Log in
                </a>
                <a
                  href={`${Config.FRONTEND_URL}/influencer-signup`}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700"
                >
                  Sign up
                </a>
              </div>
            )}
          </div>
        )}

        {/* 🔹 Desktop Nav (activeTab logic restored) */}
        <nav className="relative h-full hidden lg:flex items-center">
          <div
            ref={navContainerRef}
            className="relative flex gap-10 h-full items-center"
          >
            {["All Campaigns", "My Applied Campaigns", "My Dashboard"].map(
              (item, index) => (
                <button
                  key={item}
                  ref={(el) => (itemRefs.current[index] = el)}
                  onClick={() => {
                    // Use absolute path to ensure we stay on /brand-collaboration-live-campaigns
                    const viewParam =
                      item === "My Dashboard"
                        ? "my-dashboard"
                        : item === "My Applied Campaigns"
                        ? "my-applied-campaigns"
                        : "all-campaigns";
                    // navigate(`/brand-collaboration-live-campaigns?view=${viewParam}`);
                    goToView(viewParam);
                  }}
                  className={`${
                    activeTab === item
                      ? "font-medium text-[#2b2f34]"
                      : "font-normal text-gray-600"
                  } text-base hover:text-blue-600 transition-colors`}
                >
                  {item}
                </button>
              )
            )}

            {/* underline */}
            {activeTab && (
              <div
                className="absolute bottom-0 h-[5px] bg-blue-600 rounded transition-all duration-300 ease-in-out"
                style={underlineStyle}
              />
            )}
          </div>
        </nav>

        {/* 🔹 Desktop Profile/Auth (unchanged) */}
        {localStorage.getItem("influencer") ? (
          <>
            <div ref={dropdownRef} className="max-lg:hidden">
              <div
                className="hidden lg:flex items-center gap-3 cursor-pointer"
                onClick={() => setOpenProfile(!openProfile)}
              >
                <img
                  src={influencerAvatar}
                  alt="Profile"
                  width={40}
                  height={40}
                  className="w-10 h-10 rounded-full object-cover border border-black"
                />
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-900">
                    {JSON.parse(localStorage.getItem("influencer"))?.FullName ||
                      "User"}
                  </span>
                  <span className="text-xs text-gray-500">Creator</span>
                </div>
              </div>

              {openProfile && (
                <div className="w-fit absolute right-[90px] top-[70px] bg-white border border-gray-200 rounded-xl shadow-lg z-50 hidden lg:block">
                  <div className="flex items-center gap-3 p-4 border-b border-gray-200">
                    <img
                      src={influencerAvatar}
                      alt="Profile"
                      width={40}
                      height={40}
                      className="w-10 h-10 rounded-full object-cover border border-black"
                    />
                    <div className="flex flex-col min-w-0 max-w-full">
                      <span className="text-sm font-medium text-gray-900 break-words whitespace-normal">
                        {JSON.parse(localStorage.getItem("influencer"))
                          ?.FullName || "User"}
                      </span>
                      <span className="text-xs text-gray-500 break-words whitespace-normal">
                        {JSON.parse(localStorage.getItem("influencer"))
                          ?.Email || "Email"}
                      </span>
                    </div>
                  </div>
                  <a
                    href={`${Config.SITE_URL}/creators/profile`}
                    className="flex items-center gap-2 w-full px-4 py-3 text-sm text-gray-700 hover:bg-[#D6E8FF]"
                  >
                    <i className="fa-regular fa-id-card w-4 text-center" />
                    My Profile
                  </a>
                  <button
                    className="flex items-center gap-2 w-full px-4 py-3 text-sm text-gray-700 hover:bg-[#D6E8FF] rounded-b"
                    onClick={handleLogoutInfluencer}
                  >
                    <img src={influencerLogout} alt="" width={16} height={16} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="hidden lg:flex gap-3">
            <a
              href={`${Config.FRONTEND_URL}/influencer-login`}
              className="px-4 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              Log in
            </a>
            <a
              href={`${Config.FRONTEND_URL}/influencer-signup`}
              className="px-4 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              Sign up
            </a>
          </div>
        )}
      </div>
    </header>
  );
};

export default InfluencerHeader;
